<template>
  <div class="relative overflow-hidden">
    <CheckoutStep class="h-full">
      <template #header>
        <Stepper
          current-step-name="payment"
          :has-bouygues-steps="hasSelectedOnlineMobilePlan(items)"
          :previous-step-link="{
            name: hasSelectedOnlineMobilePlan(items)
              ? CHECKOUT.SERVICES.BOUYGUES.PHONELINE_SETUP
              : CHECKOUT.ADDRESSES,
          }"
        />
      </template>

      <template #aside>
        <CheckoutSummary
          :discount
          :discount-token
          :items
          :selected-payment-method="paymentMethod"
          show-final-taxes
          :summary
          :swapOffer
          with-extended-legal
        >
          <CheckoutSummaryAddressesCard
            v-if="addressList.bill && addressList.deliver"
            :billing="addressList.bill"
            :shipping="
              addressList?.deliverCollectionPoint || addressList.deliver
            "
          />
        </CheckoutSummary>
      </template>

      <BouyguesConfiguration
        :is-needed="hasSelectedOnlineMobilePlan(items)"
        @empty="navigateTo({ name: CHECKOUT.SERVICES.BOUYGUES.ROOT })"
      >
        <Payment
          :discount
          :items
          :payment-method
          :result-page-name="CHECKOUT.PAYMENT_RESULT"
          :summary
          :swapOffer
          @loaded="isPaymentLoading = false"
          @loading="handleStartLoading"
          @update="emit('update')"
        />
      </BouyguesConfiguration>
    </CheckoutStep>

    <RevLoadingScreen
      v-if="isPaymentLoading"
      class="bg-surface-default-mid fixed inset-0 z-10 w-full md:absolute md:right-[440px]"
      data-qa="loading-screen"
      :text="loadingMessage"
    />
  </div>
</template>

<script setup lang="ts">
import { navigateTo } from '#app'
import { onMounted, ref } from 'vue'

import type { BuybackOffer } from '@backmarket/http-api'
import type { PaymentMethod } from '@backmarket/http-api/src/api-specs-payment/payment/payment.types'
import CheckoutStep from '@backmarket/nuxt-layer-checkout/components/CheckoutStep/CheckoutStep.vue'
import CheckoutSummary from '@backmarket/nuxt-layer-checkout/components/CheckoutSummary/CheckoutSummary.vue'
import Stepper from '@backmarket/nuxt-layer-checkout/components/Stepper/Stepper.vue'
import { useCheckoutTracking } from '@backmarket/nuxt-layer-checkout/composables/useCheckoutTracking'
import { hasSelectedOnlineMobilePlan } from '@backmarket/nuxt-layer-checkout/utils/mobilePlan/hasSelectedMobilePlan'
import type { ApiSpecs } from '@backmarket/nuxt-module-http-v2/specs'
import { RevLoadingScreen } from '@ds/components/LoadingScreen'

import Payment from '~/scopes/checkout/components/Payment/Payment.vue'
import { useRedirectIfCartEmpty } from '~/scopes/checkout/composables/redirects/useRedirectIfCartEmpty'
import { useRedirectIfMissingCollectionPoint } from '~/scopes/checkout/composables/redirects/useRedirectIfMissingCollectionPoint'
import { useRedirectIfMissingShippingInformation } from '~/scopes/checkout/composables/redirects/useRedirectIfMissingShippingInformation'
import { CHECKOUT } from '~/scopes/checkout/routes-names'
import type { Discount } from '~/scopes/checkout/types/discount'

import BouyguesConfiguration from './components/BouyguesConfiguration/BouyguesConfiguration.vue'
import CheckoutSummaryAddressesCard from './components/CheckoutSummaryAddressesCard/CheckoutSummaryAddressesCard.vue'

const props = defineProps<{
  items: ApiSpecs['/bm/cart']['GET']['response']['cartItems']
  summary: ApiSpecs['/bm/cart']['GET']['response']['summary']
  swapOffer: BuybackOffer | null
  discount: Discount
  discountToken: string
  paymentMethod: PaymentMethod | null
  addressList: ApiSpecs['/bm/cart']['GET']['response']['addressList']
  billableCountries: ApiSpecs['/bm/cart']['GET']['response']['billableCountries']
  shippableCountries: ApiSpecs['/bm/cart']['GET']['response']['shippableCountries']
  lastCollectionPoint?: ApiSpecs['/bm/cart']['GET']['response']['lastCollectionPoint']
  loading: boolean
}>()

const emit = defineEmits<{
  (e: 'update'): void
}>()

const { redirectIfCartEmpty } = useRedirectIfCartEmpty('PAYMENT')
const { redirectIfMissingShippingInformation } =
  useRedirectIfMissingShippingInformation('PAYMENT')
const { redirectIfMissingCollectionPoint } =
  useRedirectIfMissingCollectionPoint('PAYMENT')
const { trackFunnel } = useCheckoutTracking()

const isPaymentLoading = ref(false)
const loadingMessage = ref('')

const handleStartLoading = (message: string) => {
  loadingMessage.value = message
  isPaymentLoading.value = true
}

onMounted(async () => {
  await redirectIfCartEmpty(props.items, CHECKOUT.CART)
  await redirectIfMissingShippingInformation(
    props.addressList,
    props.shippableCountries,
  )
  await redirectIfMissingCollectionPoint(
    props.items,
    props.shippableCountries,
    props.addressList,
    CHECKOUT.CART,
    props.lastCollectionPoint,
  )
  trackFunnel(props.items, props.swapOffer)
})
</script>
